import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { adminListSearches, adminDeleteSearch } from "@/lib/siteSearch.functions";
import { Button } from "@/components/ui/button";
import { Trash2, Search as SearchIcon, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/tools/searches")({
  head: () => ({
    meta: [
      { title: "Site Searches | Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SiteSearches,
});

type Row = Awaited<ReturnType<typeof adminListSearches>>[number];
type Range = "7" | "30" | "90" | "all";

function withinRange(createdAt: string, range: Range) {
  if (range === "all") return true;
  const days = Number(range);
  return Date.now() - new Date(createdAt).getTime() <= days * 86_400_000;
}

function SiteSearches() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<Range>("30");
  const [q, setQ] = useState("");

  async function load() {
    try {
      setError(null);
      setRows(await adminListSearches());
    } catch (e: any) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const scoped = useMemo(
    () => (rows ?? []).filter((r) => withinRange(r.created_at, range)),
    [rows, range],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter((r) => r.query.toLowerCase().includes(needle));
  }, [scoped, q]);

  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { term: string; count: number; zeroResults: number; clicks: number; last: string }
    >();
    filtered.forEach((r) => {
      const key = r.query.trim().toLowerCase();
      const existing = map.get(key);
      if (existing) {
        existing.count++;
        if ((r.results_count ?? 0) === 0) existing.zeroResults++;
        if (r.clicked_path) existing.clicks++;
        if (r.created_at > existing.last) existing.last = r.created_at;
      } else {
        map.set(key, {
          term: r.query.trim(),
          count: 1,
          zeroResults: (r.results_count ?? 0) === 0 ? 1 : 0,
          clicks: r.clicked_path ? 1 : 0,
          last: r.created_at,
        });
      }
    });
    return [...map.values()].sort((a, b) => b.count - a.count || (a.term < b.term ? -1 : 1));
  }, [filtered]);

  const totals = useMemo(() => {
    const zero = filtered.filter((r) => (r.results_count ?? 0) === 0).length;
    const clicks = filtered.filter((r) => r.clicked_path && !r.clicked_path.startsWith("requested:")).length;
    const requests = filtered.filter((r) => r.clicked_path?.startsWith("requested:")).length;
    return { searches: filtered.length, terms: grouped.length, zero, clicks, requests };
  }, [filtered, grouped]);

  async function remove(id: string) {
    if (!confirm("Delete this search record?")) return;
    await adminDeleteSearch({ data: { id } });
    load();
  }

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Site Searches</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Every search typed into the website header, so you can see what customers are looking for.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {error && (
        <p className="mt-6 border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {(["7", "30", "90", "all"] as Range[]).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={`min-h-10 border px-3 text-sm transition-colors ${
              range === r
                ? "border-brand/60 bg-primary/10 text-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {r === "all" ? "All time" : `Last ${r} days`}
          </button>
        ))}
        <div className="relative ml-auto min-w-[14rem] flex-1 md:max-w-xs">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter search terms"
            className="h-10 w-full border border-border bg-card/60 pl-9 pr-3 text-sm outline-none focus:border-brand/60"
          />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {[
          { label: "Searches", value: totals.searches },
          { label: "Unique terms", value: totals.terms },
          { label: "No results", value: totals.zero },
          { label: "Clicked a result", value: totals.clicks },
          { label: "Visitor requests", value: totals.requests },
        ].map((card) => (
          <div key={card.label} className="border border-border bg-card/40 p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{card.label}</p>
            <p className="mt-1 font-display text-2xl font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      {rows === null ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-xl font-semibold">Top search terms</h2>
            <div className="mt-3 border border-border">
              {grouped.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground">No searches recorded yet.</p>
              ) : (
                grouped.map((g) => (
                  <div
                    key={g.term.toLowerCase()}
                    className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">{g.term}</p>
                      <p className="text-xs text-muted-foreground">
                        {g.clicks} clicked · {g.zeroResults} with no results · last{" "}
                        {new Date(g.last).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="font-display text-lg font-bold">{g.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold">Recent searches</h2>
            <div className="mt-3 border border-border">
              {filtered.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground">Nothing to show.</p>
              ) : (
                filtered.slice(0, 200).map((r) => (
                  <div
                    key={r.id}
                    className="flex items-start justify-between gap-4 border-b border-border px-4 py-3 last:border-b-0"
                  >
                    <div>
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                        {r.query}
                        {r.clicked_path?.startsWith("requested:") && (
                          <span className="border border-brand/50 bg-primary/10 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-brand-text">
                            Visitor request
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()} · {r.results_count} results
                        {r.path ? ` · from ${r.path}` : ""}
                        {r.clicked_path && !r.clicked_path.startsWith("requested:")
                          ? ` · opened ${r.clicked_path}`
                          : ""}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete search record"
                      onClick={() => remove(r.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function needle_key(value: string) {
  return value.trim().toLowerCase();
}
